
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, X, ExternalLink, RefreshCw, Loader2 } from 'lucide-react'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
} from '@atrvd/saas-ui'
import {
  INTEGRATION_PROVIDERS,
  PROVIDER_CATEGORIES,
  type IntegrationProvider,
} from '@dcp/shared'
import { createTenantDb } from '@/lib/db-tenant'
import type { Integration } from '@/lib/supabase'
import { useOrganization } from '@/contexts/OrganizationContext'
import { formatDateTime } from '@dcp/shared/utils/formatters'

interface IntegrationDisplay {
  id: string
  /** Provider identifier from the shared IntegrationProvider union type */
  provider: IntegrationProvider
  name: string
  description: string
  /** Human-readable category label derived from PROVIDER_CATEGORIES */
  category: string
  status: 'connected' | 'disconnected' | 'error'
  lastSync: string | null
  integrationId?: string
}

/** The display-side status union — single source of truth, also drives the badge switch below. */
type IntegrationDisplayStatus = 'connected' | 'disconnected' | 'error'

/**
 * Narrow an arbitrary `integrations.status` value (string from Postgres) to
 * the IntegrationDisplay status union. Anything we don't recognize maps to
 * 'error' so a future enum value added on the DB side fails visibly in the UI
 * (with a red badge) instead of being silently mis-cast to 'connected'.
 *
 * Implemented as a `switch` over each literal of the union so adding a new
 * member to `IntegrationDisplayStatus` triggers a TypeScript exhaustiveness
 * error here — the `assertNeverIntegrationStatus` helper at the end of the
 * switch keeps the exhaustiveness check load-bearing rather than a
 * comment-only invariant.
 *
 * The default branch also logs a `console.warn` so a real schema-drift case
 * (a new DB enum value like `'pending'` or `'reconnecting'` not yet wired
 * into the union) surfaces in DevTools immediately instead of silently
 * collapsing every unknown status into a red "Error" badge.
 */
function normalizeIntegrationStatus(
  status: string | null | undefined,
): IntegrationDisplayStatus {
  switch (status) {
    case 'connected':
    case 'disconnected':
    case 'error':
      return status
    default:
      // Schema-drift observability: a new DB enum value reaches this
      // branch and we need to know about it. Gated by `import.meta.env.DEV`
      // so the breadcrumb lands in the developer's DevTools during local /
      // preview runs without spamming end-user consoles in production —
      // production users still see the safe-fallback red badge below.
      // When we wire up a real error-tracking service (see Security &
      // Compliance epic), this site moves to a structured `captureMessage()`
      // call alongside the DEV breadcrumb.
      if (import.meta.env.DEV && status !== null && status !== undefined && status !== '') {
        console.warn(
          `[IntegrationsPage] Unknown integration status "${status}" normalized to 'error'. ` +
            'Update IntegrationDisplayStatus to include the new value.',
        )
      }
      return 'error'
  }
}

/**
 * Exhaustiveness sentinel for `IntegrationDisplayStatus` switches below.
 * The function type signature (`_value: never`) is the load-bearing part —
 * adding a fourth status to the union without a matching `case` makes the
 * call site fail to compile because `status` is no longer `never`. At
 * runtime we degrade gracefully via the `default` branch in
 * `getStatusBadge` rather than throwing, so a hand-rolled
 * `IntegrationDisplay` (e.g. from a mock, a future direct cast, or
 * intermediate state during a refactor) can't crash the page.
 */
function assertNeverIntegrationStatus(_value: never): undefined {
  return undefined
}

function getStatusBadge(status: IntegrationDisplayStatus) {
  switch (status) {
    case 'connected':
      return (
        <Badge variant="success">
          <Check className="mr-1 h-3 w-3" />
          Connected
        </Badge>
      )
    case 'error':
      return (
        <Badge variant="destructive">
          <X className="mr-1 h-3 w-3" />
          Error
        </Badge>
      )
    case 'disconnected':
      return <Badge variant="secondary">Disconnected</Badge>
    default:
      // Compile-time exhaustiveness: a fourth member of
      // IntegrationDisplayStatus without a matching `case` makes the
      // `assertNeverIntegrationStatus(status)` call a TypeScript error
      // (`status` is no longer `never`), so the UI can't drift out of
      // sync with the union shape at build time.
      //
      // Runtime degradation: if the input bypasses TypeScript (a mock,
      // a hand-rolled cast, a Storybook story with stale data, or a
      // future direct call from JS), we render a neutral fallback
      // Badge + log a warning rather than throwing. Throwing would
      // bring down the whole IntegrationsPage tree on a single bad
      // status — way too sharp a failure mode for what's effectively
      // a display concern.
      assertNeverIntegrationStatus(status)
      // DEV-only breadcrumb — same rationale as the warn in
      // normalizeIntegrationStatus above. Production users get the
      // neutral fallback Badge silently; developers see the warning
      // immediately in local / preview runs.
      if (import.meta.env.DEV) {
        console.warn(
          `[IntegrationsPage] getStatusBadge received unexpected status "${String(status)}" — ` +
            'rendering neutral fallback. Update IntegrationDisplayStatus + this switch.',
        )
      }
      return <Badge variant="secondary">Unknown</Badge>
  }
}

export function IntegrationsPage() {
  const { currentOrganization } = useOrganization()
  const [integrations, setIntegrations] = useState<IntegrationDisplay[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  /** Load integrations from tenant database and map to display format */
  const loadIntegrations = useCallback(async () => {
    if (!currentOrganization?.id) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const tenantDb = createTenantDb(currentOrganization.id)
    const result = await tenantDb.integrations.list()

    if (result.error) {
      setError(result.error.message)
      setLoading(false)
      return
    }

    // Map connected integrations
    const connectedIntegrations = result.data.reduce(
      (acc, integration) => {
        acc[integration.provider] = integration
        return acc
      },
      {} as Record<string, Integration>
    )

    // Create display list from shared INTEGRATION_PROVIDERS constant.
    // PROVIDER_CATEGORIES maps the short category key ('erp', 'bank', 'card')
    // to the human-readable label used for section headings.
    const displayList: IntegrationDisplay[] = Object.values(INTEGRATION_PROVIDERS).map(
      (providerInfo) => {
        const connected = connectedIntegrations[providerInfo.id]

        return {
          id: providerInfo.id,
          provider: providerInfo.id,
          name: providerInfo.name,
          description: providerInfo.description,
          // Fallback to the raw category key when `PROVIDER_CATEGORIES`
          // doesn't carry a label for it — happens transiently if a new
          // category lands in the shared `INTEGRATION_PROVIDERS` constant
          // before someone updates the label map (drift between the two
          // shared exports). Without the fallback, `category` would be
          // `undefined`, and the `groupedIntegrations` reduce below would
          // create a group keyed `'undefined'` that silently swallows the
          // unmatched providers. The raw key (`'erp'`, `'bank'`, etc.) at
          // least gives the user a readable section heading and tips the
          // dev off that the label map needs updating.
          category: PROVIDER_CATEGORIES[providerInfo.category] ?? providerInfo.category,
          status: connected ? normalizeIntegrationStatus(connected.status) : 'disconnected',
          lastSync: connected?.last_sync_at || null,
          integrationId: connected?.id,
        }
      }
    )

    setIntegrations(displayList)
    setLoading(false)
  }, [currentOrganization?.id])

  useEffect(() => {
    loadIntegrations()
  }, [loadIntegrations])

  const groupedIntegrations = integrations.reduce(
    (acc, integration) => {
      if (!acc[integration.category]) {
        acc[integration.category] = []
      }
      acc[integration.category].push(integration)
      return acc
    },
    {} as Record<string, IntegrationDisplay[]>
  )

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
          <p className="text-muted-foreground">Connect and manage your financial data sources</p>
        </div>
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle>Error loading integrations</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={loadIntegrations}>Try Again</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
          <p className="text-muted-foreground">Connect and manage your financial data sources</p>
        </div>
        <Button variant="outline" onClick={loadIntegrations}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {Object.entries(groupedIntegrations).map(([category, items]) => (
        <div key={category} className="space-y-4">
          <h2 className="text-lg font-semibold">{category}</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {items.map((integration) => (
              <Card key={integration.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base">{integration.name}</CardTitle>
                      <CardDescription className="mt-1">{integration.description}</CardDescription>
                    </div>
                    {getStatusBadge(integration.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  {integration.lastSync && (
                    <p className="mb-4 text-xs text-muted-foreground">
                      Last sync: {formatDateTime(integration.lastSync)}
                    </p>
                  )}
                  <div className="flex gap-2">
                    {integration.provider === 'sage-intacct' ? (
                      // Sage Intacct: Connect → /settings/sage setup page.
                      // When already connected, show "Manage" linking to the same page.
                      <Button size="sm" className="flex-1" asChild>
                        <Link to="/settings/sage">
                          {integration.status === 'connected' ? 'Manage' : 'Connect'}
                        </Link>
                      </Button>
                    ) : integration.status === 'connected' ? (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          disabled
                          title="Integration sync coming soon"
                        >
                          <RefreshCw className="mr-2 h-3 w-3" />
                          Sync
                        </Button>
                        <Button variant="outline" size="sm" disabled title="View details coming soon">
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" className="flex-1" disabled title="Integration setup coming soon">
                        Connect
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}